import { useOutletContext } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import type { ShellOutletContext } from "../nav/AppShell";
import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import { useSupportSession } from "../support/SupportSessionProvider";
import { DutyActivityReportExport } from "../surfaces/DutyActivityReportExport";
import { ProofOfServiceExport } from "../surfaces/ProofOfServiceExport";
import { ReportsExport } from "../surfaces/ReportsExport";
import { ReportSnapshotAdmission } from "../surfaces/ReportSnapshotAdmission";

// Reports - scan-report preview + CSV export over the supervisor_scan_list read model
// (Pilot-0 "export route reports as CSV"). Pilot PDF = the browser's Print on this page.
//
// Shell v3 (product lock 2026-08-15, lock 4): the scan table is the audit trail clients pay
// for, so it fills the stage - no centered column, no soft-cap. The registry declares the
// route master-detail (the table is the master; the row drill-in record is later page-IA
// work); until the record pane exists, the head-plus-pane shape holds the table with its
// header pinned and the CSV action always in reach.
export function ReportsPage() {
  const { client, config, accountRole, can } = useOutletContext<ShellOutletContext>();
  const { user, status } = useAuth();
  const support = useSupportSession();
  const canExportProof = status === "authenticated" && Boolean(user?.id)
    && (accountRole === "admin" || accountRole === "supervisor")
    && ["report.view", "schedule.view", "evidence.view", "timeclock.manage"].every((permission) => can(permission))
    && !support.loading && support.session === null;
  return (
    <WorkbenchLayout
      className="reports-wb"
      head={
        <>
          <h1 className="page__title">Reports</h1>
          <p className="page__note">
            Filter the scan report by site, guard, and dates, preview matching captures, and
            download CSV.
          </p>
          {canExportProof && <div className="reports-proof-action">
            <ProofOfServiceExport
              client={client} actorId={user?.id ?? null} accountId={config.accountId} accountRole={accountRole} can={can}
            />
            <DutyActivityReportExport
              client={client} actorId={user?.id ?? null} accountId={config.accountId} accountRole={accountRole} can={can}
            />
          </div>}
        </>
      }
    >
      <ReportSnapshotAdmission
        client={client} accountId={config.accountId} timeZone={config.displayTimeZone}
        accountRole={accountRole} can={can}
      />
      <ReportsExport
        client={client}
        accountId={config.accountId}
        timeZone={config.displayTimeZone}
        pollMs={config.refreshMs}
      />
    </WorkbenchLayout>
  );
}
